import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lightbulb, Pencil, Check, X, Loader2, GraduationCap } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { lookupButtonHelp, type ButtonHelpResult } from "@/lib/button-help-content";
import {
  loadHelpOverrides,
  saveOverride,
  subscribe as subscribeOverrides,
  getSnapshot,
} from "@/lib/help-overrides-store";

const STORAGE_KEY = "admin_help_mode_v1";

interface HelpModeCtx {
  helpMode: boolean;
  toggle: () => void;
  setHelpMode: (v: boolean) => void;
}

const Ctx = createContext<HelpModeCtx>({
  helpMode: false,
  toggle: () => {},
  setHelpMode: () => {},
});

// State controls (Radix Switch/Checkbox/Radio render as <button role="switch|checkbox|radio">)
// must NEVER be intercepted by Help Mode — clicking them is a state change, not
// an "action", so hijacking the click would freeze the toggle in place. Same for
// inputs that happen to expose role="button" via composition. We also skip Radix
// SelectTrigger / Combobox / Tab triggers which open menus — intercepting them
// breaks every dropdown in the admin while Help Mode is on.
const STATE_CONTROL_EXCLUSIONS =
  ':not([role="switch"])' +
  ':not([role="checkbox"])' +
  ':not([role="radio"])' +
  ':not([role="combobox"])' +
  ':not([role="tab"])' +
  ':not([role="menuitem"])' +
  ':not([role="menuitemcheckbox"])' +
  ':not([role="menuitemradio"])' +
  ':not([role="option"])' +
  ':not([role="treeitem"])';

const AUTO_SELECTOR =
  'button:not([data-no-help]):not([data-help-id]):not([data-help-toggle]):not([disabled]):not([aria-disabled="true"]):not([aria-hidden="true"])' + STATE_CONTROL_EXCLUSIONS + ',' +
  'a[role="button"]:not([data-no-help]):not([aria-disabled="true"]):not([aria-hidden="true"]),' +
  '[role="button"]:not([data-no-help]):not(button):not(a):not([aria-disabled="true"]):not([aria-hidden="true"])';

function deriveLabel(el: Element): string | null {
  const aria = el.getAttribute("aria-label");
  if (aria && aria.trim()) return aria.trim();
  const title = el.getAttribute("title");
  if (title && title.trim()) return title.trim();
  const text = (el.textContent || "").trim().replace(/\s+/g, " ");
  if (text && text.length <= 120) return text;
  return null;
}

function tagElement(el: Element) {
  if (el.getAttribute("data-help-auto") === "1") return;
  const label = deriveLabel(el);
  if (!label) return;
  el.setAttribute("data-help-auto", "1");
  if (!el.getAttribute("title")) {
    el.setAttribute("title", label);
    el.setAttribute("data-help-title-set", "1");
  }
}

function untagElement(el: Element) {
  if (el.getAttribute("data-help-auto") !== "1") return;
  el.removeAttribute("data-help-auto");
  if (el.getAttribute("data-help-title-set") === "1") {
    el.removeAttribute("title");
    el.removeAttribute("data-help-title-set");
  }
}

function tagAllInAdminArea(root: ParentNode) {
  const adminAreas = root.querySelectorAll<HTMLElement>('[data-admin-area="true"]');
  adminAreas.forEach((area) => {
    area.querySelectorAll(AUTO_SELECTOR).forEach(tagElement);
  });
}

function untagAll(root: ParentNode) {
  root.querySelectorAll('[data-help-auto="1"]').forEach(untagElement);
}

function isInsideAdminArea(el: Element | null): boolean {
  let n: Element | null = el;
  while (n) {
    if (n.getAttribute && n.getAttribute("data-admin-area") === "true") return true;
    n = n.parentElement;
  }
  return false;
}

function findHelpTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  // closest() walks up to find the nearest tagged ancestor (covers icon-inside-button cases).
  const tagged = target.closest('[data-help-auto="1"]') as HTMLElement | null;
  return tagged;
}

interface ActiveHelp {
  label: string;
  testId: string | null;
  isLink: boolean;
  href: string | null;
  help: ButtonHelpResult;
  proceed: () => void;
}

interface AcademyLink {
  slug: string;
  titleAr: string;
  titleEn: string;
}

export function HelpModeProvider({ children }: { children: ReactNode }) {
  const [helpMode, setHelpModeState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [activeHelp, setActiveHelp] = useState<ActiveHelp | null>(null);
  const [academyLink, setAcademyLink] = useState<AcademyLink | null>(null);
  // Detect Arabic from <html lang> so the dialog labels stay in sync without
  // pulling i18n into the provider (which would create a circular dep).
  const [isArabic, setIsArabic] = useState<boolean>(true);

  // Platform-level overrides + inline-edit state. canEdit is true only when
  // the viewer has a platform-admin session; the API decides this for us.
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [editAr, setEditAr] = useState<string>("");
  const [editEn, setEditEn] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    loadHelpOverrides();
    setCanEdit(getSnapshot().canEdit);
    return subscribeOverrides(() => setCanEdit(getSnapshot().canEdit));
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const update = () => {
      const lang = document.documentElement.getAttribute("lang") || "ar";
      setIsArabic(lang.toLowerCase().startsWith("ar"));
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => obs.disconnect();
  }, []);

  const setHelpMode = useCallback((v: boolean) => {
    setHelpModeState(v);
    try {
      window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    } catch {}
  }, []);

  const toggle = useCallback(() => {
    setHelpModeState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.helpMode = helpMode ? "on" : "off";
    return () => {
      delete document.documentElement.dataset.helpMode;
    };
  }, [helpMode]);

  // Auto-tag every admin button/link/role=button so the CSS rule highlights it
  // and feeds the Help dialog from aria-label/text. We re-scan on every DOM
  // mutation inside admin areas so dynamically-rendered buttons (dialogs,
  // dropdowns, tabs, table rows, etc.) light up immediately.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!helpMode) {
      untagAll(document.body);
      setActiveHelp(null);
      return;
    }
    let active = true;
    let scheduled = false;
    const scan = () => {
      scheduled = false;
      if (!active) return;
      tagAllInAdminArea(document.body);
    };
    scan();
    const observer = new MutationObserver(() => {
      if (scheduled || !active) return;
      scheduled = true;
      // Coalesce bursts (table renders, route changes) into one scan per frame.
      requestAnimationFrame(scan);
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        "aria-label",
        "aria-hidden",
        "aria-disabled",
        "title",
        "disabled",
        "data-admin-area",
        "data-no-help",
      ],
    });
    return () => {
      active = false;
      observer.disconnect();
      untagAll(document.body);
    };
  }, [helpMode]);

  // While Help Mode is ON, intercept click/touch on any tagged admin control
  // and show an explanation Dialog instead of firing the button's action.
  // This is the ONLY way to make help work on touch devices (no hover).
  useEffect(() => {
    if (typeof document === "undefined" || !helpMode) return;

    const handler = (e: Event) => {
      // One-shot bypass: when proceed() re-dispatches the click after the
      // merchant pressed "Run now", we mark the event so this handler ignores
      // it. Without this the synthesized click would be intercepted again
      // and the original action would never fire.
      if ((e as any).__helpModeBypass) return;

      // Don't intercept clicks inside our own help dialog or inside any
      // other dialog the merchant explicitly opened (they're already past
      // the discovery moment for that surface).
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest("[data-help-dialog='1']")) return;
      // Allow clicks on the help-mode toggle / page-help button to work.
      if (target.closest("[data-help-toggle='1']")) return;
      if (target.closest("[data-help-id]")) return;

      // Defense in depth: even if a state control somehow ended up tagged
      // (e.g. a custom widget composes a Switch inside a button), let the
      // click through so the toggle still works.
      const stateRole = target.closest(
        '[role="switch"],[role="checkbox"],[role="radio"],[role="combobox"],[role="tab"],[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="option"],[role="treeitem"]',
      );
      if (stateRole) return;
      // Native form inputs are never "actions" either.
      if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) return;

      const tagged = findHelpTarget(target);
      if (!tagged) return;
      if (!isInsideAdminArea(tagged)) return;

      // Stop the action from firing.
      e.preventDefault();
      e.stopPropagation();
      if (typeof (e as any).stopImmediatePropagation === "function") {
        (e as any).stopImmediatePropagation();
      }

      const label = deriveLabel(tagged) || (isArabic ? "زر بدون وصف" : "Unlabeled button");
      const testId = tagged.getAttribute("data-testid");
      const href = tagged.getAttribute("href");
      const isLink = tagged.tagName === "A" && !!href;
      const route = typeof window !== "undefined" ? window.location.pathname : "";
      const help = lookupButtonHelp(testId, label, route);

      setEditing(false);
      setSaveError(null);
      setEditAr(help.ar);
      setEditEn(help.en);
      // Fetch the most relevant academy course for this control (route or
      // testId match). Failures fall through silently — academy is enrichment,
      // not core help.
      setAcademyLink(null);
      try {
        const params = new URLSearchParams();
        if (route) params.set("route", route);
        if (testId) params.set("testId", testId);
        const base = (import.meta as any).env?.BASE_URL || "/";
        fetch(`${base}api/academy/help-link?${params.toString()}`, { credentials: "include" })
          .then((r) => r.ok ? r.json() : null)
          .then((j) => { if (j?.course) setAcademyLink(j.course); })
          .catch(() => {});
      } catch {}
      setActiveHelp({
        label,
        testId,
        isLink,
        href,
        help,
        proceed: () => {
          // Re-trigger the action: for links navigate, for buttons synthesize
          // a real click marked with __helpModeBypass so our capture-phase
          // interceptor lets it through to React's synthetic handlers.
          if (isLink && href) {
            // Use anchor's own click so wouter / React Router pick it up.
            // Mark the upcoming click so the interceptor ignores it.
            const anchor = tagged as HTMLAnchorElement;
            const onceCapture = (ev: Event) => {
              (ev as any).__helpModeBypass = true;
            };
            anchor.addEventListener("click", onceCapture, { capture: true, once: true });
            anchor.click();
          } else {
            const evt = new MouseEvent("click", { bubbles: true, cancelable: true, view: window });
            (evt as any).__helpModeBypass = true;
            (tagged as HTMLElement).dispatchEvent(evt);
          }
        },
      });
    };

    // Capture phase so we beat React's synthetic handlers.
    document.addEventListener("click", handler, true);
    document.addEventListener("touchend", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("touchend", handler, true);
    };
  }, [helpMode, isArabic]);

  const closeOnly = useCallback(() => { setActiveHelp(null); setAcademyLink(null); }, []);

  return (
    <Ctx.Provider value={{ helpMode, toggle, setHelpMode }}>
      {children}
      <Dialog open={!!activeHelp} onOpenChange={(o) => !o && setActiveHelp(null)}>
        <DialogContent data-help-dialog="1" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <Lightbulb className="h-5 w-5" />
              {isArabic ? "شرح هذا الزر" : "Button help"}
            </DialogTitle>
          </DialogHeader>
          {activeHelp?.label && (
            <p className="text-sm font-semibold text-foreground -mt-2">
              {activeHelp.label}
            </p>
          )}
          {editing ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">
                  {isArabic ? "الشرح بالعربية" : "Arabic explanation"}
                </label>
                <Textarea
                  value={editAr}
                  onChange={(e) => setEditAr(e.target.value)}
                  rows={3}
                  dir="rtl"
                  data-no-help
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">
                  {isArabic ? "الشرح بالإنجليزية" : "English explanation"}
                </label>
                <Textarea
                  value={editEn}
                  onChange={(e) => setEditEn(e.target.value)}
                  rows={3}
                  dir="ltr"
                  data-no-help
                  className="text-sm"
                />
              </div>
              {saveError && (
                <p className="text-xs text-red-600">{saveError}</p>
              )}
            </div>
          ) : (
            <DialogDescription className="text-base text-foreground leading-relaxed pt-1 whitespace-pre-wrap">
              {isArabic ? activeHelp?.help.ar : activeHelp?.help.en}
            </DialogDescription>
          )}
          {activeHelp?.testId && (
            <div className="flex items-center justify-between gap-2 mt-1">
              <details className="text-xs text-muted-foreground flex-1">
                <summary className="cursor-pointer select-none">
                  {isArabic ? "تفاصيل تقنية" : "Technical details"}
                </summary>
                <p className="font-mono break-all mt-1">
                  {isArabic ? "المعرّف: " : "ID: "}
                  {activeHelp.testId}
                </p>
                <p className="mt-1">
                  {isArabic ? "المصدر: " : "Source: "}
                  {activeHelp.help.source}
                </p>
                {activeHelp.help.source === "fallback" && (
                  <p className="mt-1 text-amber-700 dark:text-amber-400">
                    {isArabic
                      ? "ملاحظة: لم يُكتب شرح مفصّل لهذا الزر بعد."
                      : "Note: a detailed help entry hasn't been written for this button yet."}
                  </p>
                )}
              </details>
              {canEdit && !editing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditAr(activeHelp.help.ar);
                    setEditEn(activeHelp.help.en);
                    setSaveError(null);
                    setEditing(true);
                  }}
                  data-no-help
                  className="text-xs gap-1 h-7"
                  title={isArabic ? "تعديل الشرح" : "Edit explanation"}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {isArabic ? "تعديل الشرح" : "Edit"}
                </Button>
              )}
            </div>
          )}
          {academyLink && !editing && (
            <a
              href={`/admin/academy/${academyLink.slug}`}
              data-no-help
              className="mt-2 flex items-center gap-2 p-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors text-sm"
              data-testid="link-academy-from-help"
            >
              <GraduationCap className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="flex-1">
                <span className="font-semibold">
                  {isArabic ? "تعلّم في الأكاديمية: " : "Learn in Academy: "}
                </span>
                {isArabic ? academyLink.titleAr : academyLink.titleEn}
              </span>
            </a>
          )}
          <p className="text-xs text-muted-foreground border-t pt-3 mt-1">
            {isArabic
              ? "وضع الشرح مفعّل. لتنفيذ الزر فعلياً اضغط (تنفيذ الآن)، أو أوقف وضع الشرح من زر المصباح في الأعلى."
              : "Help Mode is ON. Tap (Run now) to actually trigger this button, or turn Help Mode off using the lightbulb in the header."}
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            {editing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setEditing(false); setSaveError(null); }}
                  data-no-help
                  disabled={saving}
                >
                  <X className="h-4 w-4 me-1" />
                  {isArabic ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                  onClick={async () => {
                    if (!activeHelp?.testId) return;
                    const ar = editAr.trim();
                    const en = editEn.trim();
                    if (!ar || !en) {
                      setSaveError(isArabic ? "الرجاء كتابة الشرح بالعربية والإنجليزية." : "Please fill both Arabic and English.");
                      return;
                    }
                    setSaving(true);
                    setSaveError(null);
                    try {
                      await saveOverride(activeHelp.testId, ar, en);
                      setActiveHelp({
                        ...activeHelp,
                        help: { ar, en, source: "override" },
                      });
                      setEditing(false);
                    } catch (e: any) {
                      setSaveError(e?.message || (isArabic ? "تعذّر الحفظ" : "Save failed"));
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-no-help
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 me-1 animate-spin" /> : <Check className="h-4 w-4 me-1" />}
                  {isArabic ? "حفظ الشرح" : "Save explanation"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={closeOnly} data-no-help>
                  {isArabic ? "إغلاق" : "Close"}
                </Button>
                <Button
                  onClick={() => {
                    // Turn help mode off first so the synthesized click executes
                    // normally without being intercepted again.
                    setHelpMode(false);
                    const proceed = activeHelp?.proceed;
                    setActiveHelp(null);
                    requestAnimationFrame(() => proceed && proceed());
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-no-help
                >
                  {isArabic ? "تنفيذ الآن وإيقاف الشرح" : "Run now & exit help"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useHelpMode() {
  return useContext(Ctx);
}
